import React, { useCallback, useEffect } from 'react';
import { IMappedLog } from '../interface/IWorklog';
import { Checkbox, IconButton, Input, Link, Sheet, Table } from '@mui/joy';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  calculateNewInvoiceOfferTotalAmount,
  calculateOpenBudget,
  calculatePositionOpenBudget,
  calculateProjectPrice,
  deutschNumberFormatter,
  getTimeLoggedStringFormat,
} from '../helper/utils';
import { IOffer } from '../interface/IOffer';

type Props = {
  mappedLogs: IMappedLog[];
  onHourChange: (newHour: number, logIndex: number, epicIndex: number, offerIndex: number) => void;
  onMinuteChange: (newMinute: number, logIndex: number, epicIndex: number, offerIndex: number) => void;
  onOfferCheckedChange: (checked: boolean, offerIndex: number) => void;
  onOfferBookChange: (checked: boolean, offerIndex: number) => void;
  onDateChange: (newDate: string, type: string, offerIndex: number) => void;
};

function InnerRow(props: {
  row: IMappedLog['epics'][0];
  offer?: IOffer | null;
  initialOpen?: boolean;
  onHourChange: (newHour: number, logIndex: number) => void;
  onMinuteChange: (newMinute: number, logIndex: number) => void;
}) {
  const { row, onHourChange, onMinuteChange, offer } = props;
  const [open, setOpen] = React.useState(false);
  useEffect(() => {
    setOpen(false);
  }, [row]);

  const epicTotalTimeInvoiced = useCallback(() => {
    const epicTimeInvoiced = row.logs.reduce((epicTotal, log) => {
      return epicTotal + log.timeInvoiced;
    }, 0);
    return epicTimeInvoiced;
  }, [row]);

  return (
    <React.Fragment>
      <tr key={row.id}>
        <td>
          <IconButton aria-label='expand row' variant='plain' color='neutral' size='sm' onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </td>
        <td>
          <Link slotProps={{ root: { target: '_blank' } }} href={`https://public7.atlassian.net/browse/${row.key}`} underline='always'>
            {row.key}
          </Link>
        </td>
        <td>{row.position}</td>
        <td>{row.summary}</td>
        <td>{row.contractType}</td>
        <td>{row.rate}</td>
        <td>{getTimeLoggedStringFormat(epicTotalTimeInvoiced())}</td>
        <td style={{ textAlign: 'right' }}>
          {offer && offer.offerPositions
            ? deutschNumberFormatter(
                calculatePositionOpenBudget(
                  offer?.offerPositions.find((op) => op.position == row.position)?.projectPrice as number,
                  offer?.invoices!,
                  row.position,
                  offer?.DEV!,
                  offer?.PM!,
                  offer?.discount!,
                ),
              )
            : // (offer?.offerPositions.find((op) => op.position == row.position)?.projectPrice as number) -
              //   calculateProjectPrice(epicTotalTimeInvoiced() / 3600, (offer && (offer[row.rate as keyof IOffer] as number)) || 0, offer?.discount)

              '-'}
        </td>
        <td style={{ textAlign: 'right' }}>
          {deutschNumberFormatter(
            calculateProjectPrice(epicTotalTimeInvoiced() / 3600, (offer && (offer[row.rate as keyof IOffer] as number)) || 0, offer?.discount),
          )}{' '}
          €
        </td>
      </tr>
      <tr>
        <td style={{ height: 0, padding: 0 }} colSpan={9}>
          {open && (
            <Sheet
              sx={{
                display: { xs: 'none', sm: 'initial' },
                width: '100%',
                borderRadius: 'sm',
                flexShrink: 1,
                overflow: 'auto',
                minHeight: 0,
                // pl: 6,
                // p: 1,
              }}
            >
              <Table
                noWrap
                borderAxis='bothBetween'
                stickyHeader
                size='sm'
                variant='soft'
                sx={{
                  '--TableCell-headBackground': 'var(--joy-palette-background-level3)',
                  '--Table-headerUnderlineThickness': '1px',
                  '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
                  '--TableCell-paddingY': '4px',
                  '--TableCell-paddingX': '8px',
                  '& > thead > tr > th:nth-child(n + 5), & > tbody > tr > td:nth-child(n + 5)': { textAlign: 'right' },
                }}
              >
                <thead>
                  <tr>
                    <th style={{ width: '8%' }}>Project</th>
                    <th style={{ width: '4%' }}>Issue Key</th>
                    <th style={{ width: '20%' }}>Summary</th>
                    <th style={{ width: '5%' }}>Log By</th>
                    <th style={{ width: '5%' }}>Type</th>
                    <th style={{ width: '8%' }}>Start Date</th>
                    <th style={{ width: '5%' }}>Time Logged</th>
                    <th style={{ width: '10%' }}>Time Invoiced</th>
                  </tr>
                </thead>
                <tbody>
                  {row.logs
                    .sort((a, b) => {
                      if (a.issueId !== b.issueId) {
                        return a.issueId - b.issueId;
                      }
                      return new Date(`${a.startDate} ${a.startTime}`).getTime() - new Date(`${b.startDate} ${b.startTime}`).getTime();
                    })
                    .map((log, logIndex) => (
                      <tr key={log.tempoWorklogId}>
                        <td>
                          {log.issue.project.key}/{log.issue.project.name}
                        </td>
                        <td>
                          <Link
                            slotProps={{ root: { target: '_blank' } }}
                            href={`https://public7.atlassian.net/browse/${log.issue.key}`}
                            underline='always'
                          >
                            {log.issue.key}
                          </Link>
                        </td>
                        <td>{log.issue.summary}</td>
                        <td>{log.jiraUser}</td>
                        <td>{log.issue.type}</td>
                        <td>
                          {log.startDate} {log.startTime}
                        </td>
                        <td>{getTimeLoggedStringFormat(log.timeLogged)}</td>
                        <td>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-end',
                            }}
                          >
                            <Input
                              onChange={(e) => {
                                onHourChange(Number(e.target.value), logIndex);
                              }}
                              value={log.timeInvoicedSplit.h}
                              slotProps={{
                                input: {
                                  min: '0',
                                  type: 'number',
                                  placeholder: '0',
                                  style: { textAlign: 'right' },
                                },
                              }}
                              sx={{ width: '30%' }}
                              size='sm'
                            />
                            <span style={{ marginLeft: 5, marginRight: 10 }}>h</span>
                            <Input
                              value={log.timeInvoicedSplit.m}
                              onChange={(e) => {
                                let newMinute = Number(e.target.value);
                                if (newMinute === 60) {
                                  onHourChange(log.timeInvoicedSplit.h + 1, logIndex);
                                  onMinuteChange(0, logIndex);
                                } else {
                                  onMinuteChange(newMinute, logIndex);
                                }
                              }}
                              slotProps={{
                                input: {
                                  min: '0',
                                  type: 'number',
                                  placeholder: '0',
                                  style: { textAlign: 'right' },
                                  step: '5',
                                  max: '60',
                                },
                              }}
                              sx={{ width: '30%' }}
                              size='sm'
                            />
                            <span style={{ marginLeft: 5 }}>m</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </Sheet>
          )}
        </td>
      </tr>
    </React.Fragment>
  );
}

function Row(props: {
  row: IMappedLog;
  initialOpen?: boolean;
  onHourChange: (newHour: number, logIndex: number, epicIndex: number) => void;
  onMinuteChange: (newMinute: number, logIndex: number, epicIndex: number) => void;
  onOfferCheckedChange: (checked: boolean) => void;
  onOfferBookChange: (checked: boolean) => void;
  onDateChange: (newDate: string, type: string) => void;
}) {
  const { row, onHourChange, onMinuteChange, onOfferCheckedChange, onDateChange, onOfferBookChange } = props;
  const [open, setOpen] = React.useState(props.initialOpen || false);

  useEffect(() => {
    setOpen(false);
  }, [row]);

  const offerTotalTimeInvoiced = useCallback(() => {
    const totalTimeInvoiced = row.epics.reduce((total, epic) => {
      const epicTimeInvoiced = epic.logs.reduce((epicTotal, log) => {
        return epicTotal + log.timeInvoiced;
      }, 0);
      return total + epicTimeInvoiced;
    }, 0);
    return totalTimeInvoiced;
  }, [row]);

  return (
    <React.Fragment>
      <tr>
        <td>
          <IconButton aria-label='expand row' variant='plain' color='neutral' size='sm' onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </td>
        <td>{row.offerId}</td>
        <td>{getTimeLoggedStringFormat(offerTotalTimeInvoiced())}</td>
        <td>
          <Checkbox checked={row.checked} onChange={(e) => onOfferCheckedChange(e.target.checked)} />
        </td>
        <td>
          <Checkbox checked={row.book} onChange={(e) => onOfferBookChange(e.target.checked)} />
        </td>
        <td>{row.checked && <Input type='date' size='sm' value={row.invoiceFrom!} onChange={(e) => onDateChange(e.target.value, 'from')} />}</td>
        <td>{row.checked && <Input type='date' size='sm' value={row.invoiceTo!} onChange={(e) => onDateChange(e.target.value, 'to')} />}</td>
        <td>
          {deutschNumberFormatter(
            calculateOpenBudget(row.offer?.offerValue!, row.offer?.invoices ?? [], row.offer?.DEV!, row.offer?.PM!, row.offer?.discount),
          )}{' '}
          €
        </td>
        <td>{deutschNumberFormatter(calculateNewInvoiceOfferTotalAmount(row?.offer || null, row.epics))} €</td>
      </tr>
      <tr>
        <td style={{ height: 0, padding: 0 }} colSpan={12}>
          {open && (
            <Sheet
              sx={{
                display: { xs: 'none', sm: 'initial' },
                width: '100%',
                borderRadius: 'sm',
                flexShrink: 1,
                overflow: 'auto',
                minHeight: 0,
              }}
            >
              <Table
                noWrap
                borderAxis='y'
                size='sm'
                // sx={{
                //   '& > thead > tr > th:nth-child(n + 3), & > tbody > tr > td:nth-child(n + 3)': { textAlign: 'right' },
                //   '--TableCell-paddingX': '0.5rem',
                // }}
                sx={{
                  '--TableCell-headBackground': 'var(--joy-palette-background-level2)',
                  '--Table-headerUnderlineThickness': '1px',
                  '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
                  '--TableCell-paddingY': '4px',
                  '--TableCell-paddingX': '8px',
                }}
              >
                <thead>
                  <tr>
                    <th style={{ width: 40 }} aria-label='empty' />
                    <th style={{ width: '15%' }}>Epic Key</th>
                    <th style={{ width: '8%' }}>Position</th>
                    <th style={{ width: '90%' }}>Epic Summary</th>
                    <th style={{ width: '20%' }}>Contract Type</th>
                    <th style={{ width: '10%' }}>Rate</th>
                    <th style={{ width: '30' }}>Time Invoiced</th>
                    <th style={{ width: '20%' }}>Open Budget</th>
                    <th style={{ width: '20%' }}>Total Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {row.epics
                    .sort((a, b) => a.position - b.position)
                    .map((innerRow, epicIndex) => (
                      <InnerRow
                        offer={row.offer}
                        key={innerRow.id}
                        row={innerRow}
                        initialOpen={epicIndex === 0}
                        onHourChange={(newHour, logIndex) => {
                          onHourChange(newHour, logIndex, epicIndex);
                        }}
                        onMinuteChange={(newMinute, logIndex) => {
                          onMinuteChange(newMinute, logIndex, epicIndex);
                        }}
                      />
                    ))}
                </tbody>
              </Table>
            </Sheet>
          )}
        </td>
      </tr>
    </React.Fragment>
  );
}

const NewInvoiceTable = ({ mappedLogs, onHourChange, onMinuteChange, onOfferCheckedChange, onOfferBookChange, onDateChange }: Props) => {
  return (
    <Sheet
      variant='outlined'
      sx={{
        display: { xs: 'none', sm: 'initial' },
        width: '100%',
        borderRadius: 'sm',
        flexShrink: 1,
        overflow: 'auto',
        minHeight: 0,
      }}
    >
      <Table
        // borderAxis="bothBetween"
        size='sm'
        sx={{
          '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
          '--Table-headerUnderlineThickness': '1px',
          '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
          '--TableCell-paddingY': '4px',
          '--TableCell-paddingX': '8px',
        }}
      >
        <thead>
          <tr>
            <th style={{ width: 40 }} aria-label='empty' />
            <th style={{ width: '20%' }}>Offer Id</th>
            <th style={{ width: '20%' }}>Time Invoiced</th>
            <th style={{ width: '10%' }}>Invoice</th>
            <th style={{ width: '10%' }}>Book</th>
            <th style={{ width: '20%' }}>From</th>
            <th style={{ width: '20%' }}>To</th>
            <th style={{ width: '20%' }}>Open Budget</th>
            <th style={{ width: '20%' }}>Total Amount</th>
          </tr>
        </thead>
        <tbody>
          {mappedLogs.map((row, offerIndex) => (
            <Row
              key={row.offerId}
              row={row}
              initialOpen={offerIndex === 0}
              onHourChange={(newHour, logIndex, epicIndex) => {
                onHourChange(newHour, logIndex, epicIndex, offerIndex);
              }}
              onMinuteChange={(newMinute, logIndex, epicIndex) => {
                onMinuteChange(newMinute, logIndex, epicIndex, offerIndex);
              }}
              onOfferCheckedChange={(checked) => onOfferCheckedChange(checked, offerIndex)}
              onOfferBookChange={(checked) => onOfferBookChange(checked, offerIndex)}
              onDateChange={(newDate, type) => onDateChange(newDate, type, offerIndex)}
            />
          ))}
        </tbody>
      </Table>
    </Sheet>
  );
};

export default NewInvoiceTable;
